import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ApiDatabase from 'server';
import { PAGE_SIZE } from './useMonthInvoices';

// The most the api gives in one page: ticking a row reads its invoices by
// pages of this many.
export const SELECT_PAGE_SIZE = 100;

/**
 * What narrows the flat list to the invoices of a row of the grouped list
 * (JBWEB-1309): a group by `groupId`, a company or the invoices of no company
 * ("none") by `companyId`.
 */
export const rowFilterOf = (group) =>
  group.type === 'group' ? { groupId: group.id } : { companyId: group.id };

/**
 * The invoices of one row of the grouped list (JBWEB-1309), read from the flat
 * list with the filters of the grouped one (`listQuery`) and the filter of the
 * row: one page at a time once the row is unfolded, and all of them when the
 * row is ticked.
 *
 * The row is folded at first. Unfolded, its first page is read; folded again,
 * it comes back to that page. When the grouped list is read again
 * (`refreshedWith` changes: a new answer), an unfolded row is read again
 * quietly, so that its invoices follow the totals above them.
 *
 * Each row reads with its own abort key: the reads of the list and of the
 * other rows share the url, and would otherwise cancel each other.
 */
const useGroupInvoices = (group, listQuery, refreshedWith) => {
  const { type, id } = group;
  const abortKey = `admin/invoices#row:${type}:${id}`;
  const rowFilter = useMemo(() => rowFilterOf({ type, id }), [type, id]);

  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [outcome, setOutcome] = useState(null);
  const answer = outcome?.page === page ? outcome : null;
  const list = isOpen ? (answer?.list ?? null) : null;
  const failed = isOpen && Boolean(answer?.failed);
  const isLoading = isOpen && !list && !failed;

  const latestRead = useRef(0);

  const read = useCallback(
    (isQuiet) => {
      latestRead.current += 1;
      const readId = latestRead.current;
      if (!isQuiet) setOutcome(null);
      ApiDatabase.getMonthInvoices(
        { ...listQuery, ...rowFilter, page, limit: PAGE_SIZE },
        (data) => {
          if (readId === latestRead.current) setOutcome({ page, list: data });
        },
        (error) => {
          if (readId !== latestRead.current) return;
          if (error?.name === 'CanceledError') return;
          // A quiet read that fails leaves the invoices as last read.
          if (!isQuiet) setOutcome({ page, failed: true });
        },
        { abortKey },
      );
    },
    [listQuery, rowFilter, page, abortKey],
  );

  // A read for a page left is dropped by the next one; one for a row folded
  // answers into nothing shown, and unfolding reads again.
  useEffect(() => {
    if (isOpen) read(false);
  }, [isOpen, read]);

  const seenRefresh = useRef(refreshedWith);
  useEffect(() => {
    if (seenRefresh.current === refreshedWith) return;
    seenRefresh.current = refreshedWith;
    if (isOpen) read(true);
  }, [refreshedWith, isOpen, read]);

  const toggleOpen = useCallback(() => {
    setIsOpen((open) => !open);
    setPage(1);
  }, []);

  const reload = useCallback(() => read(false), [read]);

  // ── Every invoice of the row, for its box ────────────────────────
  const [isSelecting, setIsSelecting] = useState(false);
  const latestSelect = useRef(0);

  useEffect(
    () => () => {
      // A row gone (another month, filter or page of rows) ticks nothing.
      latestSelect.current += 1;
    },
    [],
  );

  /**
   * Reads every invoice of the row, a page of `SELECT_PAGE_SIZE` after the
   * other, then gives them to `onRead`; `onFailed` if a page cannot be read.
   */
  const readAll = useCallback(
    (onRead, onFailed) => {
      latestSelect.current += 1;
      const readId = latestSelect.current;
      const invoices = [];
      setIsSelecting(true);
      const readPage = (pageToRead) =>
        ApiDatabase.getMonthInvoices(
          {
            ...listQuery,
            ...rowFilter,
            page: pageToRead,
            limit: SELECT_PAGE_SIZE,
          },
          (data) => {
            if (readId !== latestSelect.current) return;
            const items = data?.items ?? [];
            invoices.push(...items);
            // An empty page ends it too: never a loop on pages that never come.
            if (pageToRead < (data?.pages ?? 1) && items.length > 0) {
              readPage(pageToRead + 1);
              return;
            }
            setIsSelecting(false);
            onRead(invoices);
          },
          (error) => {
            if (readId !== latestSelect.current) return;
            setIsSelecting(false);
            if (error?.name === 'CanceledError') return;
            onFailed();
          },
          { abortKey: `${abortKey}#select` },
        );
      readPage(1);
    },
    [listQuery, rowFilter, abortKey],
  );

  return {
    isOpen,
    toggleOpen,
    list,
    isLoading,
    failed,
    page,
    setPage,
    reload,
    readAll,
    isSelecting,
  };
};

export default useGroupInvoices;
